/**
 * LivestreamBlock — Standard plan
 * Toggle "LIVE NOW", YouTube/Facebook channel + current stream IDs.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Radio } from "lucide-react";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function LivestreamBlock({ churchId }) {
  const [data, setData] = useState({
    youtube_channel_url: "",
    youtube_video_id: "",
    facebook_page_url: "",
    facebook_video_id: "",
    is_live: false,
    schedule_text: "",
    notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!churchId) return;
    (async () => {
      try {
        const { data: row } = await api.get(`/church-builder/churches/${churchId}/livestream`);
        if (row && Object.keys(row).length) setData({ ...data, ...row });
      } catch { /* empty livestream is ok */ }
      finally { setLoading(false); }
    })();
     
  }, [churchId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/church-builder/churches/${churchId}/livestream`, data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="luxury-card rounded-xl p-6"><Loader2 className="animate-spin text-[#D4AF37]" size={16} /></div>;

  return (
    <div data-testid="cb-livestream-block" className="luxury-card rounded-xl p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-serif-display text-2xl flex items-center gap-3">Livestream
            {data.is_live && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] tracking-widest uppercase font-bold border border-red-500/60 bg-red-900/30 text-red-200"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE NOW</span>}
          </div>
          <div className="text-xs text-neutral-400 mt-1">Embed YouTube + Facebook live streams. Toggle visibility manually.</div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">YouTube channel URL</div>
          <input data-testid="cb-livestream-yt-url" className={inp} placeholder="https://youtube.com/@yourchurch" value={data.youtube_channel_url} onChange={(e) => setData({ ...data, youtube_channel_url: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">YouTube video ID (current live)</div>
          <input data-testid="cb-livestream-yt-vid" className={inp} placeholder="e.g. dQw4w9WgXcQ" value={data.youtube_video_id} onChange={(e) => setData({ ...data, youtube_video_id: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Facebook page URL</div>
          <input data-testid="cb-livestream-fb-url" className={inp} placeholder="https://facebook.com/yourchurch" value={data.facebook_page_url} onChange={(e) => setData({ ...data, facebook_page_url: e.target.value })} />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Facebook video ID (current live)</div>
          <input className={inp} value={data.facebook_video_id} onChange={(e) => setData({ ...data, facebook_video_id: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Schedule (shown when not live)</div>
          <input className={inp} placeholder="Sundays · 10:30 GMT" value={data.schedule_text} onChange={(e) => setData({ ...data, schedule_text: e.target.value })} />
        </div>
      </div>

      <label className="mt-5 inline-flex items-center gap-3 text-sm text-white" data-testid="cb-livestream-toggle">
        <input type="checkbox" checked={!!data.is_live} onChange={(e) => setData({ ...data, is_live: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
        <span className="inline-flex items-center gap-2"><Radio size={14} className="text-red-400" /> Show &quot;LIVE NOW&quot; badge on public site</span>
      </label>

      <div className="mt-6">
        <button data-testid="cb-livestream-save" disabled={saving} onClick={save} className="btn-gold px-5 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save livestream
        </button>
      </div>
    </div>
  );
}
